(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const collect = (root, callback) => {
    if (root?.matches?.('[data-pzr-story-carousel]')) callback(root);
    root?.querySelectorAll?.('[data-pzr-story-carousel]').forEach(callback);
  };

  const initialize = (carousel) => {
    if (carousel.dataset.pzrStoryCarouselReady === 'true') return;

    const viewport = carousel.querySelector('[data-pzr-story-carousel-viewport]');
    const track = carousel.querySelector('.pzr-story-carousel__track');
    const primary = carousel.querySelector('[data-pzr-story-carousel-primary]');
    const clone = carousel.querySelector('[data-pzr-story-carousel-clone]');
    const cards = [...(primary?.querySelectorAll('[data-pzr-story-carousel-card]') || [])];
    if (!viewport || !track || !primary || !clone || cards.length === 0) {
      carousel.hidden = true;
      return;
    }

    carousel.dataset.pzrStoryCarouselReady = 'true';
    let frame = 0;
    let last = 0;
    let resumeAt = 0;
    let dragging = false;
    let dragMoved = false;
    let suppressClick = false;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let autoPosition = viewport.scrollLeft;
    let hovered = false;
    let focusWithin = false;
    let touchActive = false;
    let inView = false;
    const speed = 16;

    const gap = (element) => {
      const styles = getComputedStyle(element);
      return Number.parseFloat(styles.columnGap || styles.gap) || 0;
    };

    const loopLength = () => primary.scrollWidth + gap(track);
    const canMove = () => cards.length > 1 && viewport.scrollWidth > viewport.clientWidth + 2;

    const pauseFor = (delay = 900) => {
      resumeAt = Math.max(resumeAt, performance.now() + delay);
    };

    const normalizeLoop = () => {
      const resetAt = loopLength();
      if (resetAt <= 0) return;
      let next = viewport.scrollLeft;
      while (next >= resetAt) next -= resetAt;
      while (next < 0) next += resetAt;
      viewport.scrollLeft = next;
      autoPosition = next;
    };

    const motionPaused = (time) => (
      reduceMotion.matches || !inView || document.hidden || hovered || focusWithin || touchActive || dragging || time < resumeAt
    );

    const tick = (time) => {
      if (!last) last = time;
      const delta = Math.min(40, time - last);
      last = time;
      if (canMove() && !motionPaused(time)) {
        const resetAt = loopLength();
        autoPosition += speed * (delta / 1000);
        if (resetAt > 0) while (autoPosition >= resetAt) autoPosition -= resetAt;
        viewport.scrollLeft = autoPosition;
      } else {
        autoPosition = viewport.scrollLeft;
      }
      frame = requestAnimationFrame(tick);
    };

    const move = (direction) => {
      const step = Math.max(cards[0].getBoundingClientRect().width + gap(primary), viewport.clientWidth * 0.65);
      viewport.scrollBy({ left: direction * step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      pauseFor(1200);
      window.setTimeout(normalizeLoop, reduceMotion.matches ? 0 : 500);
    };

    const onPointerEnter = (event) => {
      if (event.pointerType === 'mouse') hovered = true;
    };
    const onPointerLeave = (event) => {
      if (event.pointerType === 'mouse') {
        hovered = false;
        pauseFor(180);
      }
    };
    const onFocusIn = () => { focusWithin = true; };
    const onFocusOut = () => {
      window.setTimeout(() => {
        focusWithin = viewport.contains(document.activeElement);
        if (!focusWithin) pauseFor(250);
      }, 0);
    };
    const onTouchStart = () => { touchActive = true; };
    const onTouchEnd = () => { touchActive = false; pauseFor(); };
    const onWheel = (event) => {
      if (Math.abs(event.deltaX) > 0.5) pauseFor();
    };
    const onDragStart = (event) => event.preventDefault();
    const onPointerDown = (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      dragging = true;
      dragMoved = false;
      suppressClick = false;
      dragStartX = event.clientX;
      dragStartScroll = viewport.scrollLeft;
      viewport.classList.add('is-dragging');
    };
    const onPointerMove = (event) => {
      if (!dragging) return;
      const delta = event.clientX - dragStartX;
      if (!dragMoved && Math.abs(delta) > 5) {
        dragMoved = true;
        viewport.setPointerCapture?.(event.pointerId);
      }
      if (!dragMoved) return;
      viewport.scrollLeft = dragStartScroll - delta;
      event.preventDefault();
    };
    const endDrag = (event) => {
      if (!dragging) return;
      suppressClick = dragMoved;
      dragging = false;
      viewport.classList.remove('is-dragging');
      if (viewport.hasPointerCapture?.(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      normalizeLoop();
      pauseFor();
    };
    const onClick = (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    };
    const onKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    };
    const onMotionChange = () => {
      last = 0;
      autoPosition = viewport.scrollLeft;
    };

    viewport.addEventListener('pointerenter', onPointerEnter);
    viewport.addEventListener('pointerleave', onPointerLeave);
    viewport.addEventListener('focusin', onFocusIn);
    viewport.addEventListener('focusout', onFocusOut);
    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchend', onTouchEnd, { passive: true });
    viewport.addEventListener('touchcancel', onTouchEnd, { passive: true });
    viewport.addEventListener('wheel', onWheel, { passive: true });
    viewport.addEventListener('dragstart', onDragStart);
    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);
    viewport.addEventListener('click', onClick, true);
    viewport.addEventListener('keydown', onKeydown);
    reduceMotion.addEventListener?.('change', onMotionChange);

    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(normalizeLoop) : null;
    resizeObserver?.observe(viewport);
    resizeObserver?.observe(primary);
    const intersectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      inView = entries.some((entry) => entry.isIntersecting);
      last = 0;
    }, { threshold: 0.05 }) : null;
    if (intersectionObserver) intersectionObserver.observe(carousel);
    else inView = true;

    frame = requestAnimationFrame(tick);

    carousel.pzrStoryCarouselCleanup = () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      viewport.removeEventListener('pointerenter', onPointerEnter);
      viewport.removeEventListener('pointerleave', onPointerLeave);
      viewport.removeEventListener('focusin', onFocusIn);
      viewport.removeEventListener('focusout', onFocusOut);
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchend', onTouchEnd);
      viewport.removeEventListener('touchcancel', onTouchEnd);
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('dragstart', onDragStart);
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', endDrag);
      viewport.removeEventListener('pointercancel', endDrag);
      viewport.removeEventListener('click', onClick, true);
      viewport.removeEventListener('keydown', onKeydown);
      reduceMotion.removeEventListener?.('change', onMotionChange);
      delete carousel.dataset.pzrStoryCarouselReady;
      delete carousel.pzrStoryCarouselCleanup;
    };
  };

  const destroy = (carousel) => carousel.pzrStoryCarouselCleanup?.();

  collect(document, initialize);
  document.addEventListener('shopify:section:load', (event) => collect(event.target, initialize));
  document.addEventListener('shopify:section:unload', (event) => collect(event.target, destroy));
})();
